import { alpha } from '@mui/material/styles';

/**
 * Branding tokens do tenant Dr. Herança.
 * Baseado na linguagem visual da landing page para reutilização na área logada.
 */
export const drHerancaBranding = {
  color: {
    accent: {
      main: '#9B2335',
      light: '#FDEAED',
    },
    text: {
      primary: '#1A1A1A',
      secondary: 'rgba(0,0,0,0.64)',
      menu: 'rgba(0,0,0,0.74)',
      inverse: '#FFFFFF',
    },
    surface: {
      base: '#FFFFFF',
      subtle: '#FAFAFA',
      icon: '#F1F3F5',
      inverse: '#1A1A1A',
    },
    border: {
      default: 'rgba(0,0,0,0.08)',
      strong: 'rgba(0,0,0,0.18)',
    },
  },
  radius: {
    pill: '50px',
    card: '16px',
    cardLarge: '24px',
    iconBox: '12px',
  },
  shadow: {
    cardHover: '0 20px 40px rgba(0,0,0,0.08)',
    heroMedia: '0 30px 60px rgba(0,0,0,0.12)',
  },
  spacing: {
    card: 4,
    cardLarge: 5,
  },
  fontWeight: {
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
} as const;

export type DrHerancaBranding = typeof drHerancaBranding;

/**
 * Presets de estilo para acelerar adoção visual.
 * Podem ser usados diretamente em `sx`.
 */
export const drHerancaBrandingPresets = {
  iconBox: {
    width: 48,
    height: 48,
    borderRadius: drHerancaBranding.radius.iconBox,
    bgcolor: drHerancaBranding.color.surface.icon,
    color: drHerancaBranding.color.accent.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardSurface: {
    borderRadius: drHerancaBranding.radius.card,
    border: `1px solid ${drHerancaBranding.color.border.default}`,
    bgcolor: drHerancaBranding.color.surface.base,
    boxShadow: 'none',
  },
  cardInteractive: {
    borderRadius: drHerancaBranding.radius.card,
    border: `1px solid ${drHerancaBranding.color.border.default}`,
    bgcolor: drHerancaBranding.color.surface.base,
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: drHerancaBranding.color.accent.main,
      transform: 'translateY(-4px)',
      boxShadow: drHerancaBranding.shadow.cardHover,
    },
  },
  cardInteractiveOutlineOnly: {
    borderRadius: drHerancaBranding.radius.card,
    border: `1px solid ${drHerancaBranding.color.border.default}`,
    bgcolor: drHerancaBranding.color.surface.base,
    boxShadow: 'none',
    transition: 'border-color 0.3s ease',
    '&:hover': {
      borderColor: drHerancaBranding.color.accent.main,
      transform: 'none',
      boxShadow: 'none',
    },
  },
  cardInverse: {
    borderRadius: drHerancaBranding.radius.cardLarge,
    bgcolor: drHerancaBranding.color.surface.inverse,
    color: drHerancaBranding.color.text.inverse,
    border: 'none',
  },
  buttonPrimary: {
    bgcolor: drHerancaBranding.color.accent.main,
    color: drHerancaBranding.color.text.inverse,
    borderRadius: drHerancaBranding.radius.pill,
    fontWeight: drHerancaBranding.fontWeight.semibold,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: drHerancaBranding.color.text.primary,
      boxShadow: 'none',
    },
  },
  buttonOutlined: {
    borderColor: drHerancaBranding.color.border.strong,
    color: drHerancaBranding.color.text.primary,
    borderRadius: drHerancaBranding.radius.pill,
    fontWeight: drHerancaBranding.fontWeight.semibold,
    '&:hover': {
      borderColor: drHerancaBranding.color.text.primary,
      bgcolor: 'transparent',
    },
  },
  sectionHighlight: {
    background: `radial-gradient(circle, ${alpha(drHerancaBranding.color.accent.main, 0.08)} 0%, transparent 50%)`,
  },
} as const;

